/**
 * RBAC Admin — runtime role & permission management.
 *
 * Tabs:
 *   Roles        — list + create + patch + delete roles
 *   Permissions  — list + inspect grants per permission
 *   Grants       — bulk-edit which permissions a role holds
 *   Audit        — append-only log of RBAC mutations
 */

import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useRbac } from '../../context/RbacContext';

// ── Shared style helpers ──────────────────────────────────────────────────────

const card = {
  background: '#fff',
  borderRadius: 10,
  boxShadow: '0 1px 4px rgba(0,0,0,.08)',
  padding: '20px 24px',
  marginBottom: 20,
};

const btn = (color = '#94335a', outline = false) => ({
  padding: '6px 14px',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  border: outline ? `1px solid ${color}` : 'none',
  background: outline ? 'transparent' : color,
  color: outline ? color : '#fff',
  transition: 'opacity .15s',
});

const badge = (color) => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 12,
  fontSize: 11,
  fontWeight: 600,
  background: color + '22',
  color: color,
  border: `1px solid ${color}55`,
  marginRight: 4,
  marginBottom: 2,
});

const CATEGORY_COLORS = { page: '#7c3aed', action: '#dc2626', api: '#0284c7', field: '#059669' };

function CategoryBadge({ cat }) {
  return (
    <span style={badge(CATEGORY_COLORS[cat] || '#666')}>{cat}</span>
  );
}

function SystemBadge() {
  return <span style={badge('#d97706')}>system</span>;
}

// ── Data helpers ──────────────────────────────────────────────────────────────

const unwrap = (resp) => resp?.data?.data ?? resp?.data ?? resp;

async function fetchRoles()       { return unwrap(await api.get('/rbac/roles')); }
async function fetchPermissions() { return unwrap(await api.get('/rbac/permissions')); }
async function fetchAudit(limit=100, offset=0) {
  return unwrap(await api.get('/rbac/audit', { params: { limit, offset } }));
}

// ── Tab: Roles ────────────────────────────────────────────────────────────────

function RolesTab() {
  const [roles, setRoles]   = useState([]);
  const [busy,  setBusy]    = useState(false);
  const [form,  setForm]    = useState({ code: '', display: '', description: '', is_system: false });
  const [error, setError]   = useState('');
  const { reload: reloadRbac } = useRbac();

  const load = useCallback(async () => {
    try { setRoles(await fetchRoles()); } catch (e) { setError(String(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.code || !form.display) return setError('Code and display name are required.');
    setBusy(true); setError('');
    try {
      await api.post('/rbac/roles', form);
      setForm({ code: '', display: '', description: '', is_system: false });
      await load();
      reloadRbac();
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally { setBusy(false); }
  };

  const toggle = async (role) => {
    setBusy(true);
    try {
      await api.patch(`/rbac/roles/${role.role_code}`, { is_active: !role.is_active });
      await load(); reloadRbac();
    } finally { setBusy(false); }
  };

  const del = async (role) => {
    if (!window.confirm(`Delete role ${role.role_code}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/rbac/roles/${role.role_code}`);
      await load(); reloadRbac();
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally { setBusy(false); }
  };

  return (
    <div>
      {/* Create form */}
      <div style={card}>
        <h3 style={{ margin: '0 0 14px', fontSize: 15 }}>New Role</h3>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: 1, minWidth: 120 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 3 }}>Code</div>
            <input
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 5, boxSizing: 'border-box' }}
              placeholder="BILLING"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\W/, '') })}
            />
          </label>
          <label style={{ flex: 2, minWidth: 160 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 3 }}>Display name</div>
            <input
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 5, boxSizing: 'border-box' }}
              placeholder="Billing Staff"
              value={form.display}
              onChange={(e) => setForm({ ...form, display: e.target.value })}
            />
          </label>
          <label style={{ flex: 3, minWidth: 200 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 3 }}>Description</div>
            <input
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 5, boxSizing: 'border-box' }}
              placeholder="Optional description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </label>
          <button style={btn('#94335a')} onClick={create} disabled={busy}>
            {busy ? 'Creating…' : '+ Add Role'}
          </button>
        </div>
        {error && <p style={{ color: '#dc2626', margin: '10px 0 0', fontSize: 13 }}>{error}</p>}
      </div>

      {/* Roles table */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #ece3e7' }}>
              {['Code', 'Display', 'Description', 'Flags', 'Sort', 'Status', ''].map((h) => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#8b7681' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => (
              <tr key={r.role_code} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontWeight: 600 }}>{r.role_code}</td>
                <td style={{ padding: '10px 14px' }}>{r.display_name}</td>
                <td style={{ padding: '10px 14px', color: '#8b7681' }}>{r.description || '—'}</td>
                <td style={{ padding: '10px 14px' }}>{r.is_system ? <SystemBadge /> : null}</td>
                <td style={{ padding: '10px 14px', color: '#8b7681' }}>{r.sort_order}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={badge(r.is_active ? '#16a34a' : '#dc2626')}>
                    {r.is_active ? 'active' : 'inactive'}
                  </span>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <button style={{ ...btn('#475569', true), marginRight: 6 }} onClick={() => toggle(r)} disabled={busy || r.is_system}>
                    {r.is_active ? 'Disable' : 'Enable'}
                  </button>
                  {!r.is_system && (
                    <button style={btn('#dc2626')} onClick={() => del(r)} disabled={busy}>Delete</button>
                  )}
                </td>
              </tr>
            ))}
            {roles.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#a99aa0' }}>No roles found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tab: Permissions ──────────────────────────────────────────────────────────

function PermissionsTab() {
  const [perms,    setPerms]   = useState([]);
  const [filter,   setFilter]  = useState('');
  const [catFilter, setCat]    = useState('');
  const [error,    setError]   = useState('');

  const load = useCallback(async () => {
    try { setPerms(await fetchPermissions()); } catch (e) { setError(String(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = perms.filter((p) => {
    const key = (p.permission_key || p.key || '').toLowerCase();
    if (filter && !key.includes(filter.toLowerCase())) return false;
    if (catFilter && (p.category || '') !== catFilter) return false;
    return true;
  });

  const categories = [...new Set(perms.map((p) => p.category).filter(Boolean))];

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <input
          style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, flex: 1 }}
          placeholder="Search permissions…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <select
          style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
          value={catFilter}
          onChange={(e) => setCat(e.target.value)}
        >
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {error && <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p>}

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #ece3e7' }}>
              {['Permission Key', 'Category', 'Self Param', 'Granted Roles', 'Description'].map((h) => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#8b7681' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => {
              const key = p.permission_key || p.key;
              return (
                <tr key={key} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12 }}>{key}</td>
                  <td style={{ padding: '10px 14px' }}><CategoryBadge cat={p.category} /></td>
                  <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 11, color: '#8b7681' }}>
                    {p.self_param || '—'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    {(p.roles || []).map((r) => (
                      <span key={r} style={badge('#94335a')}>{r}</span>
                    ))}
                    {(!p.roles || p.roles.length === 0) && <span style={{ color: '#a99aa0', fontSize: 11 }}>none</span>}
                  </td>
                  <td style={{ padding: '10px 14px', color: '#8b7681' }}>{p.description || '—'}</td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#a99aa0' }}>No permissions found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tab: Grants ───────────────────────────────────────────────────────────────

function GrantsTab() {
  const [roles,         setRoles]      = useState([]);
  const [perms,         setPerms]      = useState([]);
  const [selectedRole,  setRole]       = useState('');
  const [granted,       setGranted]    = useState(new Set());
  const [pending,       setPending]    = useState(new Set());
  const [busy,          setBusy]       = useState(false);
  const [saved,         setSaved]      = useState(false);
  const { reload: reloadRbac } = useRbac();

  const load = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([fetchRoles(), fetchPermissions()]);
      setRoles(r); setPerms(p);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedRole) { setGranted(new Set()); setPending(new Set()); return; }
    const role = (roles || []).find((r) => r.role_code === selectedRole);
    // permissions list has roles[] per permission
    const g = new Set(
      (perms || [])
        .filter((p) => (p.roles || []).includes(selectedRole))
        .map((p) => p.permission_key || p.key)
    );
    setGranted(g);
    setPending(new Set(g));
    setSaved(false);
  }, [selectedRole, roles, perms]);

  const toggle = (pkey) => {
    setPending((prev) => {
      const next = new Set(prev);
      next.has(pkey) ? next.delete(pkey) : next.add(pkey);
      return next;
    });
    setSaved(false);
  };

  const save = async () => {
    if (!selectedRole) return;
    setBusy(true); setSaved(false);
    try {
      await api.put(`/rbac/roles/${selectedRole}/grants`, {
        permission_keys: Array.from(pending),
      });
      await load();
      reloadRbac();
      setSaved(true);
    } catch (e) {
      alert(e?.response?.data?.detail || String(e));
    } finally { setBusy(false); }
  };

  const changed = [...pending].sort().join() !== [...granted].sort().join();
  const byCategory = {};
  (perms || []).forEach((p) => {
    const cat = p.category || 'api';
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(p);
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <select
          style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, minWidth: 180 }}
          value={selectedRole}
          onChange={(e) => setRole(e.target.value)}
        >
          <option value="">— Select a role —</option>
          {(roles || []).map((r) => (
            <option key={r.role_code} value={r.role_code}>{r.display_name} ({r.role_code})</option>
          ))}
        </select>
        {selectedRole && (
          <>
            <button style={btn(changed ? '#94335a' : '#a99aa0')} onClick={save} disabled={busy || !changed}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
            {saved && <span style={{ color: '#16a34a', fontSize: 13 }}>✓ Saved</span>}
            {changed && <span style={{ color: '#d97706', fontSize: 12 }}>Unsaved changes</span>}
          </>
        )}
      </div>

      {selectedRole && Object.entries(byCategory).map(([cat, catPerms]) => (
        <div key={cat} style={{ ...card, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <CategoryBadge cat={cat} />
            <span style={{ fontSize: 13, color: '#8b7681' }}>{catPerms.length} permissions</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
            {catPerms.map((p) => {
              const pkey = p.permission_key || p.key;
              const checked = pending.has(pkey);
              return (
                <label key={pkey} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer',
                  padding: '8px 10px', borderRadius: 6,
                  background: checked ? '#fdeef5' : '#f8fafc',
                  border: `1px solid ${checked ? '#93c5fd' : '#ece3e7'}`,
                }}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(pkey)} style={{ marginTop: 2 }} />
                  <div>
                    <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{pkey}</div>
                    {p.description && (
                      <div style={{ fontSize: 11, color: '#8b7681', marginTop: 1 }}>{p.description}</div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      ))}

      {!selectedRole && (
        <div style={{ textAlign: 'center', padding: 48, color: '#a99aa0' }}>
          Select a role above to manage its permission grants
        </div>
      )}
    </div>
  );
}

// ── Tab: Audit ────────────────────────────────────────────────────────────────

const ACTION_COLORS = {
  'role.create': '#16a34a', 'role.delete': '#dc2626', 'role.patch': '#d97706',
  'grant.add':   '#0284c7', 'grant.remove': '#7c3aed', 'grant.replace': '#0891b2',
  'perm.create': '#16a34a', 'perm.delete': '#dc2626',
  'partyrole.assign': '#0284c7', 'partyrole.revoke': '#7c3aed',
};

function AuditTab() {
  const [rows,   setRows]   = useState([]);
  const [offset, setOffset] = useState(0);
  const [total,  setTotal]  = useState(null);
  const LIMIT = 50;

  const load = useCallback(async (off = 0) => {
    try {
      const data = await fetchAudit(LIMIT, off);
      setRows(Array.isArray(data) ? data : data?.items ?? []);
      if (data?.total !== undefined) setTotal(data.total);
    } catch {}
  }, []);

  useEffect(() => { load(offset); }, [load, offset]);

  return (
    <div>
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #ece3e7' }}>
              {['Time', 'Actor', 'Action', 'Target', 'Before', 'After'].map((h) => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#8b7681' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const color = ACTION_COLORS[r.action] || '#475569';
              return (
                <tr key={r.audit_id ?? i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '9px 14px', color: '#8b7681', whiteSpace: 'nowrap' }}>
                    {r.at ? new Date(r.at).toLocaleString() : '—'}
                  </td>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontSize: 11, color: '#8b7681' }}>
                    {r.actor_id ? r.actor_id.slice(0, 8) + '…' : 'system'}
                  </td>
                  <td style={{ padding: '9px 14px' }}>
                    <span style={badge(color)}>{r.action}</span>
                  </td>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontSize: 12 }}>{r.target}</td>
                  <td style={{ padding: '9px 14px', color: '#a99aa0', fontSize: 11, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.before_val ? JSON.stringify(r.before_val) : '—'}
                  </td>
                  <td style={{ padding: '9px 14px', color: '#475569', fontSize: 11, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.after_val ? JSON.stringify(r.after_val) : '—'}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#a99aa0' }}>No audit entries</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 10 }}>
        <button style={btn('#475569', true)} onClick={() => setOffset(Math.max(0, offset - LIMIT))} disabled={offset === 0}>
          ← Prev
        </button>
        <span style={{ alignSelf: 'center', fontSize: 13, color: '#8b7681' }}>
          {offset + 1}–{offset + rows.length}{total != null ? ` of ${total}` : ''}
        </span>
        <button style={btn('#475569', true)} onClick={() => setOffset(offset + LIMIT)} disabled={rows.length < LIMIT}>
          Next →
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'roles',       label: '🎭 Roles' },
  { key: 'permissions', label: '🔑 Permissions' },
  { key: 'grants',      label: '🔗 Grants' },
  { key: 'audit',       label: '📋 Audit' },
];

export default function RbacAdmin() {
  const [tab, setTab] = useState('roles');
  const { version, loading, reload } = useRbac();

  return (
    <div style={{ padding: '24px 28px', fontFamily: 'Inter, system-ui, sans-serif', maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>🛡️ Roles & Permissions</h1>
          <p style={{ margin: '4px 0 0', color: '#8b7681', fontSize: 13 }}>
            Runtime RBAC administration — all changes take effect immediately.
            {!loading && <span style={{ color: '#a99aa0', marginLeft: 8 }}>Config v{version}</span>}
          </p>
        </div>
        <button style={btn('#475569', true)} onClick={reload} disabled={loading}>
          {loading ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #ece3e7' }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: tab === t.key ? 700 : 400,
              color: tab === t.key ? '#94335a' : '#8b7681',
              borderBottom: tab === t.key ? '2px solid #94335a' : '2px solid transparent',
              marginBottom: -2,
              borderRadius: '6px 6px 0 0',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'roles'       && <RolesTab />}
      {tab === 'permissions' && <PermissionsTab />}
      {tab === 'grants'      && <GrantsTab />}
      {tab === 'audit'       && <AuditTab />}
    </div>
  );
}
